import { useState, useRef, useCallback, useEffect } from 'react';
import PCMCapture from '@/lib/audio-capture';

/**
 * WebSocket + voice-IO hook for the staff Voice Intake page.
 *
 * Input:  raw 16-bit PCM @ 16 kHz mono (via Web Audio API + AudioWorklet)
 * Output: 24 kHz PCM from Gemini Live — queued and played back-to-back so
 *         the agent's voice doesn't stutter between chunks.
 */
const useAudioStream = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);

  const [toolResults, setToolResults] = useState([]);
  const [isThinking, setIsThinking] = useState(false);
  const [industryType, setIndustryType] = useState(null);

  const wsRef = useRef(null);
  const captureRef = useRef(null);

  // Playback state — one AudioContext @ 24 kHz, scheduled queue
  const playbackCtxRef = useRef(null);
  const playbackCursorRef = useRef(0);

  // WebSocket URL derived from env or current origin
  const getWebSocketUrl = (tenantId) => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    if (BACKEND_URL) {
      const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      return `${WS_URL}/ws/stream/${tenantId}`;
    }
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/ws/stream/${tenantId}`;
  };

  /** Decode & schedule a 24 kHz PCM chunk from Gemini; chunks play seamlessly. */
  const playAudio = async (base64Audio) => {
    try {
      if (!playbackCtxRef.current) {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        // 24 kHz matches Gemini Live output; browsers will resample to device rate.
        playbackCtxRef.current = new AudioCtx({ sampleRate: 24000 });
        playbackCursorRef.current = playbackCtxRef.current.currentTime;
      }
      const ctx = playbackCtxRef.current;
      if (ctx.state === 'suspended') await ctx.resume();

      const binary = atob(base64Audio);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

      const sampleCount = bytes.length / 2;
      const audioBuffer = ctx.createBuffer(1, sampleCount, 24000);
      const channelData = audioBuffer.getChannelData(0);
      const dv = new DataView(bytes.buffer);
      for (let i = 0; i < sampleCount; i++) {
        channelData[i] = dv.getInt16(i * 2, true) / 0x8000;
      }

      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);

      // Schedule so chunks play back-to-back rather than overlapping/truncating.
      const startAt = Math.max(ctx.currentTime, playbackCursorRef.current);
      source.start(startAt);
      playbackCursorRef.current = startAt + audioBuffer.duration;
    } catch (err) {
      console.error('Error playing audio:', err);
    }
  };

  const handleMessage = useCallback(async (event) => {
    try {
      const data = JSON.parse(event.data);
      switch (data.type) {
        case 'session_started':
          setSessionId(data.session_id);
          setIndustryType(data.industry_type || null);
          console.log('Session started:', data.session_id, 'Industry:', data.industry_type);
          break;

        case 'audio':
          if (data.audio) await playAudio(data.audio);
          break;

        case 'transcript':
          setTranscript((prev) => {
            const incoming = data.text || '';
            const last = prev[prev.length - 1];
            // Merge consecutive streaming fragments from the SAME speaker into
            // one growing message — otherwise each word becomes its own line.
            if (last && last.speaker === data.speaker && last.streaming) {
              const merged = { ...last, text: (last.text || '') + incoming };
              return [...prev.slice(0, -1), merged];
            }
            return [
              ...prev,
              {
                id: Date.now() + Math.random(),
                text: incoming,
                speaker: data.speaker,
                timestamp: new Date().toISOString(),
                type: 'message',
                streaming: true,
              },
            ];
          });
          setIsThinking(false);
          break;

        case 'turn_complete':
          console.log('Turn complete');
          setIsThinking(true);
          // Finalize the current streaming message so the next turn starts a new bubble.
          setTranscript((prev) => {
            if (!prev.length) return prev;
            const last = prev[prev.length - 1];
            if (last.streaming) {
              return [...prev.slice(0, -1), { ...last, streaming: false }];
            }
            return prev;
          });
          // Reset playback cursor so next agent turn doesn't queue behind old audio.
          if (playbackCtxRef.current) {
            playbackCursorRef.current = playbackCtxRef.current.currentTime;
          }
          break;

        case 'interrupted':
          console.log('Interrupted');
          setIsThinking(false);
          break;

        case 'tool_result': {
          const toolResult = {
            tool_name: data.tool_name,
            result: data.result,
            timestamp: data.timestamp || new Date().toISOString(),
            id: Date.now() + Math.random(),
          };
          setToolResults((prev) => [...prev, toolResult]);
          setTranscript((prev) => [
            ...prev,
            {
              id: Date.now() + Math.random() + 1,
              text: `Tool: ${data.tool_name} - ${JSON.stringify(data.result, null, 2)}`,
              speaker: 'system',
              timestamp: toolResult.timestamp,
              type: 'tool_result',
              tool_name: data.tool_name,
              tool_result: data.result,
            },
          ]);
          setIsThinking(false);
          break;
        }

        case 'error':
          setError(data.message || 'Unknown error occurred');
          console.error('WebSocket error:', data.message);
          setIsThinking(false);
          break;

        default:
          console.log('Unknown message type:', data.type, data);
      }
    } catch (err) {
      console.error('Error parsing WebSocket message:', err);
    }
  }, []);

  const stopRecording = useCallback(async () => {
    if (captureRef.current) {
      await captureRef.current.stop();
      captureRef.current = null;
    }
    setIsRecording(false);
  }, []);

  const disconnect = useCallback(async () => {
    await stopRecording();

    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch (_) {
        /* ignore */
      }
      wsRef.current = null;
    }

    if (playbackCtxRef.current && playbackCtxRef.current.state !== 'closed') {
      try {
        await playbackCtxRef.current.close();
      } catch (_) {
        /* ignore */
      }
      playbackCtxRef.current = null;
    }

    setIsConnected(false);
    setSessionId(null);
    setTranscript([]);
    setToolResults([]);
    setIsThinking(false);
    setIndustryType(null);
  }, [stopRecording]);

  const connect = useCallback(
    (tenantId) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) disconnect();

      try {
        const wsUrl = getWebSocketUrl(tenantId);
        wsRef.current = new WebSocket(wsUrl);

        wsRef.current.onopen = () => {
          setIsConnected(true);
          setError(null);
          console.log('WebSocket connected');
        };

        wsRef.current.onmessage = handleMessage;

        wsRef.current.onclose = (ev) => {
          setIsConnected(false);
          setIsRecording(false);
          setIsThinking(false);
          console.log('WebSocket disconnected', { code: ev.code, reason: ev.reason });
        };

        wsRef.current.onerror = (err) => {
          setError('WebSocket connection failed');
          console.error('WebSocket error:', err);
        };
      } catch (err) {
        setError('Failed to connect to WebSocket');
        console.error('Connection error:', err);
      }
    },
    [handleMessage, disconnect]
  );

  const startRecording = useCallback(async () => {
    if (!isConnected || isRecording) return;

    try {
      const cap = new PCMCapture({
        onChunk: (b64) => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'audio', audio: b64 }));
          }
        },
        onError: (e) => {
          console.error('PCMCapture error:', e);
          setError(e?.message || 'Microphone error');
        },
        onReady: ({ sampleRate }) =>
          console.log(`[PCMCapture] ready — sampleRate=${sampleRate}`),
      });

      await cap.start();
      captureRef.current = cap;
      setIsRecording(true);
      setError(null);
    } catch (err) {
      setError('Failed to access microphone. Please allow mic access and retry.');
      console.error('Recording error:', err);
    }
  }, [isConnected, isRecording]);

  const clearTranscript = useCallback(() => setTranscript([]), []);
  const clearToolResults = useCallback(() => setToolResults([]), []);

  useEffect(() => {
    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    connect,
    disconnect,
    startRecording,
    stopRecording,
    clearTranscript,
    clearToolResults,
    isConnected,
    isRecording,
    transcript,
    sessionId,
    error,
    toolResults,
    isThinking,
    industryType,
  };
};

export default useAudioStream;
